import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { stanAnalytics } from '@/lib';
import { DropdownMenuOption, DropdownValue } from './StanDropdown.types';
import { useUser } from '@/queries/useUser';

interface DropdownContextValue {
    isOpen: boolean;
    internalValue: DropdownValue | null;
    setIsOpen: (isOpen: boolean) => void;
    handleSelect: (
        option: DropdownMenuOption,
        onSelect: (value: DropdownValue, option: DropdownMenuOption) => void,
        keepOpen?: boolean
    ) => void;
    setInternalValue: (value: DropdownValue | null) => void;
}

const DropdownContext = createContext<DropdownContextValue | undefined>(undefined);

interface DropdownProviderProps {
    children: React.ReactNode;
    initialValue?: DropdownValue | null;
    dropdownId?: string;
    dropdownName?: string;
}

export const DropdownProvider = ({
    children,
    initialValue = null,
    dropdownId,
    dropdownName,
}: DropdownProviderProps) => {
    const [internalValue, setInternalValue] = useState<DropdownValue | null>(initialValue);
    const [isOpen, setIsOpenState] = useState(false);
    const { user } = useUser();
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Sync internal value with currentValue prop changes
    useEffect(() => {
        setInternalValue(initialValue);
    }, [initialValue]);

    const handleSelect = useCallback(
        (
            option: DropdownMenuOption,
            onSelect: (value: DropdownValue, option: DropdownMenuOption) => void,
            _keepOpen = false
        ) => {
            if (option.disabled) return;

            if (dropdownName) {
                stanAnalytics('admin-dropdown-option-click', {
                    meta: {
                        user_id: user?.user_id,
                        username: user?.username,
                    },
                    props: {
                        dropdown_name: dropdownName,
                        dropdown_value: option.value,
                        option_label: option.label,
                    },
                });
            }

            const dropdownValue: DropdownValue = { label: option.label, value: option.value };
            onSelect(dropdownValue, option);
        },
        [dropdownName, user?.user_id, user?.username]
    );

    const handleOpen = useCallback(
        (newIsOpen: boolean) => {
            if (dropdownName && isMountedRef.current) {
                stanAnalytics(`admin-${newIsOpen ? 'show' : 'hide'}-dropdown`, {
                    meta: { user_id: user?.user_id },
                    props: {
                        dropdown_menu_name: dropdownName,
                        dropdown_id: dropdownId,
                    },
                });
            }

            setIsOpenState(newIsOpen);
        },
        [dropdownId, dropdownName, user?.user_id]
    );

    const contextValue: DropdownContextValue = useMemo(
        () => ({
            isOpen,
            internalValue,
            setIsOpen: handleOpen,
            handleSelect,
            setInternalValue,
        }),
        [handleOpen, handleSelect, internalValue, isOpen]
    );

    return <DropdownContext.Provider value={contextValue}>{children}</DropdownContext.Provider>;
};

export const useDropdown = () => {
    const context = useContext(DropdownContext);
    if (!context) {
        throw new Error('useDropdown must be used within a DropdownProvider');
    }
    return context;
};
